import { useEffect, useState } from "react";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, ShieldCheck, AlertTriangle, Info, Send } from "lucide-react";

export default function AgentDeposit() {
  const [data, setData] = useState(null);
  const [req, setReq] = useState(null); // adjustment being intimated
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => api.get("/agent/deposit").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const sendIntimation = async () => {
    setSending(true);
    try {
      await api.post(`/agent/deposit-adjustments/${req.id}/request-refill`, { note });
      toast.success("Intimation sent - the company will verify the payment and refill your deposit");
      setReq(null); setNote("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (!data) return <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  const m = (v) => money(v, data.currency, data.symbol);
  const pct = data.credit_used_pct;
  const STATUS_LABEL = { proposed: "Under review", deducted: "Adjusted from deposit", refill_requested: "Intimation sent", refilled: "Refilled" };
  const STATUS_VARIANT = { proposed: "secondary", deducted: "destructive", refill_requested: "default", refilled: "outline" };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Deposit & credit</h1>
        <p className="text-sm text-muted-foreground">A refundable security deposit — held in trust, not income.</p>
      </div>

      {data.topup_required && (
        <Card className="p-4 bg-destructive/10 border-destructive/30 flex items-start gap-3" data-testid="topup-alert">
          <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-destructive">Top-up required{data.deposit_shortfall > 0 && ` · ${m(data.deposit_shortfall)} short`}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              An amount was adjusted from your deposit for overdue customer invoices. It is refilled automatically once the customer pays and you send the intimation below.
            </p>
          </div>
        </Card>
      )}

      {data.adjustments?.length > 0 && (
        <div data-testid="agent-adjustments">
          <h3 className="font-semibold text-sm mb-2">Invoice adjustments against your deposit</h3>
          <div className="space-y-2">
            {data.adjustments.map((a) => (
              <Card key={a.id} className="p-4" data-testid={`agent-adjustment-${a.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-mono text-xs text-muted-foreground">{a.invoice_no}</p>
                    <p className="text-sm font-medium">{a.customer_name}</p>
                    <p className="text-xs text-muted-foreground">Invoice {a.invoice_date} · due {a.due_date}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-destructive">−{m(a.amount)}</p>
                    <Badge variant={STATUS_VARIANT[a.status]} className="mt-1">{STATUS_LABEL[a.status] || a.status}</Badge>
                  </div>
                </div>
                {a.status === "deducted" && (
                  <Button size="sm" className="w-full mt-3" onClick={() => setReq(a)} data-testid={`request-refill-${a.id}`}>
                    <Send className="w-3.5 h-3.5 mr-1.5" /> Customer has paid — request deposit refill
                  </Button>
                )}
                {a.status === "refill_requested" && <p className="text-xs text-muted-foreground mt-2">Sent {a.refill_requested_at?.slice(0, 10)} · awaiting company verification{a.refill_note && ` · "${a.refill_note}"`}</p>}
                {a.status === "refilled" && <p className="text-xs text-primary mt-2">Refilled to your deposit on {a.refilled_at?.slice(0, 10)}</p>}
              </Card>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!req} onOpenChange={(v) => !v && setReq(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Intimate payment · {req?.invoice_no}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Confirm that <b>{req?.customer_name}</b> has cleared this invoice. The company will verify the receipt and refill {req && m(req.amount)} into your deposit.</p>
          <Input placeholder="Optional note (e.g. paid by transfer on 12 Jul)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="refill-note-input" />
          <DialogFooter>
            <Button onClick={sendIntimation} disabled={sending} data-testid="send-intimation-button">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send intimation"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="p-5" data-testid="deposit-summary">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground">Refundable deposit held</p>
            <p className="font-display text-3xl font-extrabold tracking-tight">{m(data.deposit_held)}</p>
          </div>
          <Badge variant="secondary" className="gap-1"><ShieldCheck className="w-3 h-3" /> {data.tier}</Badge>
        </div>
      </Card>

      {/* Credit gauge */}
      <Card className="p-5">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-muted-foreground">Credit used</span>
          <span className={data.over_limit ? "text-destructive font-semibold" : "font-medium"}>
            {m(data.credit_used)} / {m(data.credit_limit)}
          </span>
        </div>
        <div className="h-3 bg-secondary rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-700 ${data.over_limit ? "bg-destructive" : data.near_limit ? "bg-amber-500" : "bg-accent"}`}
            style={{ width: `${Math.min(100, pct)}%` }}
          />
        </div>
        <div className="flex justify-between mt-2 text-xs">
          <span className="text-muted-foreground">{pct}% used</span>
          <span className="text-primary font-medium">{m(data.credit_available)} available</span>
        </div>
      </Card>

      {(data.stock_held?.length > 0 || data.unpaid_invoices_value > 0) && (
        <Card className="p-4 text-sm" data-testid="agent-stock-held">
          <p className="text-xs text-muted-foreground mb-2">What makes up your credit used</p>
          {data.stock_held.map((h) => (
            <div key={h.product} className="flex justify-between py-1 border-b border-border/40"><span>{h.qty} × {h.product} <span className="text-muted-foreground">(unsold, on credit)</span></span><span>{m(h.value)}</span></div>
          ))}
          <div className="flex justify-between py-1"><span className="text-muted-foreground">Unpaid customer invoices</span><span>{m(data.unpaid_invoices_value)}</span></div>
        </Card>
      )}

      {data.form && (
        <Card className="p-4 text-sm">
          <p className="text-muted-foreground text-xs mb-1">Deposit form on file</p>
          <p className="font-medium capitalize">{data.form.replace(/_/g, " ")} · {data.form_reference || "—"}</p>
        </Card>
      )}

      {/* Ledger */}
      <div>
        <h3 className="font-semibold text-sm mb-2">Deposit ledger</h3>
        <div className="space-y-2">
          {data.ledger.map((e) => (
            <Card key={e.id} className="p-3 flex items-start justify-between" data-testid={`ledger-${e.id}`}>
              <div className="flex-1 pr-3">
                <p className="text-sm capitalize font-medium">{e.type}</p>
                <p className="text-xs text-muted-foreground">{e.reason}</p>
              </div>
              <span className={`text-sm font-semibold ${e.type === "deduction" ? "text-destructive" : "text-primary"}`}>
                {e.type === "deduction" ? "−" : "+"}{m(e.amount)}
              </span>
            </Card>
          ))}
        </div>
      </div>

      <Card className="p-4 bg-secondary/40 flex items-start gap-3">
        <Info className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
        <p className="text-xs text-muted-foreground leading-relaxed">
          Your deposit equals your stock credit limit, so the company is covered for unpaid bags you hold.
          It is fully refundable within 30 days of leaving in good standing, once all stock and customer
          payments are settled. It is never treated as commission or income.
        </p>
      </Card>
    </div>
  );
}
